import {useState} from 'react';

import {Button, CheckBox, Input, Select} from '@/components';
import {Filter} from '@/components/Filter';
import type {AffiliateReferralListParams} from '@/types';
import {BusinessReferralStatus} from '@/types';
import {capitalizeWords} from '@/utils/dashboard';

const STATUS_OPTIONS = [
  {key: 'All statuses', value: ''},
  ...Object.values(BusinessReferralStatus).map((status) => ({
    key: capitalizeWords(status.replace(/_/g, ' ')),
    value: status,
  })),
];

const BUSINESS_ID_PATTERN = /^[a-f0-9]{24}$/i;
const BUSINESS_ID_HINT = 'Enter the full 24-character business ID';

const isInvalidId = (value?: string): boolean =>
  Boolean(value) && !BUSINESS_ID_PATTERN.test(value as string);

type AffiliateReferralsFiltersProps = {
  filterCount: number;
  filtersToApply: AffiliateReferralListParams;
  clearFilters: () => void;
  selectAppliedFilters: (
    name: string,
    value: string | boolean | undefined,
  ) => void;
  handleApplyFilters: () => void;
};

export default function AffiliateReferralsFilters({
  filterCount,
  filtersToApply,
  clearFilters,
  selectAppliedFilters,
  handleApplyFilters,
}: AffiliateReferralsFiltersProps): React.JSX.Element {
  const [showFilters, setShowFilters] = useState<boolean>(false);

  const hasInvalidId =
    isInvalidId(filtersToApply.referrerBusinessId) ||
    isInvalidId(filtersToApply.refereeBusinessId);

  const handleApplyFilter = (): void => {
    handleApplyFilters();
    setShowFilters(false);
  };

  return (
    <div className="w-full">
      <div className="flex items-center space-x-3">
        <Filter
          closeFilter={() => setShowFilters(false)}
          handleShowFilter={() => setShowFilters(!showFilters)}
          label="Filters"
          positionLeft={1}
          bodyClassName="w-full md:w-[600px]"
        >
          {showFilters && (
            <div className="p-4 space-y-4 max-w-4xl mx-auto w-full col-span-full">
              <Select
                label="Status"
                value={filtersToApply.status ?? ''}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  selectAppliedFilters('status', e.target.value)
                }
                options={STATUS_OPTIONS}
                className="w-full"
                data-testid="affiliate-referrals-status"
              />

              <Input
                label="Referrer business ID"
                name="referrerBusinessId"
                type="text"
                value={filtersToApply.referrerBusinessId ?? ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  selectAppliedFilters(
                    'referrerBusinessId',
                    e.target.value.trim(),
                  )
                }
                className="w-full isFull"
                placeholder="24-character ID"
                error={
                  isInvalidId(filtersToApply.referrerBusinessId)
                    ? BUSINESS_ID_HINT
                    : undefined
                }
              />

              <Input
                label="Referee business ID"
                name="refereeBusinessId"
                type="text"
                value={filtersToApply.refereeBusinessId ?? ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  selectAppliedFilters(
                    'refereeBusinessId',
                    e.target.value.trim(),
                  )
                }
                className="w-full isFull"
                placeholder="24-character ID"
                error={
                  isInvalidId(filtersToApply.refereeBusinessId)
                    ? BUSINESS_ID_HINT
                    : undefined
                }
              />

              <CheckBox
                label="Only referrals flagged as possible self-referrals"
                checked={Boolean(filtersToApply.isFlagged)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  selectAppliedFilters('isFlagged', e.target.checked)
                }
              />

              <div className="flex md:justify-end">
                <Button
                  onClick={clearFilters}
                  colorScheme="cyan"
                  variant="ghost"
                >
                  Clear
                </Button>
                <Button
                  onClick={handleApplyFilter}
                  colorScheme="cyan"
                  disabled={filterCount === 0 || hasInvalidId}
                  className="ml-3"
                  data-testid="affiliate-referrals-apply-filters"
                >
                  Apply Filters {filterCount ? ` (${filterCount})` : ''}
                </Button>
              </div>
            </div>
          )}
        </Filter>
      </div>
    </div>
  );
}
